interface HastText {
  type: 'text';
  value: string;
}

interface HastElement {
  type: 'element';
  tagName: string;
  properties: Record<string, unknown>;
  children: HastNode[];
}

type HastNode = HastText | HastElement | { type: string; children?: HastNode[] };

const SKIP = new Set(['code', 'pre', 'kbd', 'samp', 'script', 'style']);
const OPEN_CONTEXT = /[\s([{—–-]/;
const LETTER = /[\p{L}\p{N}]/u;

function isText(node: HastNode): node is HastText {
  return node.type === 'text';
}

function isElement(node: HastNode): node is HastElement {
  return node.type === 'element';
}

export function smartenQuotes(value: string, before: string): string {
  let out = '';
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i];
    const prev = i === 0 ? before : value[i - 1];
    const next = value[i + 1] ?? '';
    if (ch === '"') {
      out += prev === '' || OPEN_CONTEXT.test(prev) ? '“' : '”';
    } else if (ch === "'") {
      if (LETTER.test(prev) && LETTER.test(next)) out += '’';
      else out += prev === '' || OPEN_CONTEXT.test(prev) ? '‘' : '’';
    } else {
      out += ch;
    }
  }
  return out;
}

function smarten(node: HastNode, before: { last: string }) {
  if (isElement(node) && SKIP.has(node.tagName)) {
    before.last = 'x';
    return;
  }
  if (isText(node)) {
    node.value = smartenQuotes(node.value, before.last);
    if (node.value.length > 0) before.last = node.value[node.value.length - 1];
    return;
  }
  if ('children' in node && node.children) for (const child of node.children) smarten(child, before);
}

function speechSpan(children: HastNode[]): HastElement {
  return { type: 'element', tagName: 'span', properties: { className: ['md-quote-inline'] }, children };
}

function wrapSpeech(parent: HastElement) {
  const out: HastNode[] = [];
  let open: { index: number; head: string } | null = null;
  const pending: HastNode[] = [];

  const flushOpen = () => {
    if (open === null) return;
    out.push({ type: 'text', value: open.head } as HastText, ...pending);
    pending.length = 0;
    open = null;
  };

  for (const child of parent.children) {
    if (!isText(child)) {
      if (open === null) out.push(child);
      else pending.push(child);
      continue;
    }
    let rest = child.value;
    while (rest.length > 0) {
      if (open === null) {
        const start = rest.indexOf('“');
        if (start === -1) {
          out.push({ type: 'text', value: rest } as HastText);
          rest = '';
          break;
        }
        if (start > 0) out.push({ type: 'text', value: rest.slice(0, start) } as HastText);
        rest = rest.slice(start);
        const end = rest.indexOf('”', 1);
        if (end !== -1) {
          out.push(speechSpan([{ type: 'text', value: rest.slice(0, end + 1) } as HastText]));
          rest = rest.slice(end + 1);
        } else {
          open = { index: out.length, head: rest };
          rest = '';
        }
      } else {
        const end = rest.indexOf('”');
        if (end === -1) {
          pending.push({ type: 'text', value: rest } as HastText);
          rest = '';
        } else {
          const head = open.head;
          out.push(speechSpan([{ type: 'text', value: head } as HastText, ...pending, { type: 'text', value: rest.slice(0, end + 1) } as HastText]));
          pending.length = 0;
          open = null;
          rest = rest.slice(end + 1);
        }
      }
    }
  }
  flushOpen();
  parent.children = out;
}

function walk(node: HastNode) {
  if (!isElement(node) && !(node.type === 'root')) return;
  if (isElement(node) && SKIP.has(node.tagName)) return;
  const children = 'children' in node && node.children ? node.children : [];
  for (const child of children) walk(child);
  if (isElement(node) && children.some(isText)) wrapSpeech(node);
}

export function rehypeQuotedSpeech() {
  return (tree: HastNode) => {
    smarten(tree, { last: '' });
    walk(tree);
  };
}
